// ---------------------------------------------------------------------------
// How the Assessment Agent's controls look (NDI-239 v2)
//
// The design system's own CSS is in the build since R12b1 but switched off
// until R12b2 (tailwind.css imports it under `(width < 0px)`), so a component
// from ../design that styles itself with sdr- classes would show unstyled
// today. And daisyUI's classes and colour names may only become fewer (the
// ratchet, R12a). So these modules draw with Tailwind's layout and type
// utilities, and take every colour from the theme layer that is active now:
// the --color-* and --sdr-* variables of entrypoints/sdr/src/client/tailwind.css,
// restated there for dark mode and high contrast. Buttons and fields copy the
// look of today's pages: navy only on the primary action, hairlines, soft
// radii, the focus ring of the theme.
//
// Every class is written whole here, so Tailwind finds it.
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';

import { icon } from '../design/icons.js';

const BUTTON =
  'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const BUTTON_SIZE = {
  sm: 'h-8 px-3 text-sm',
  xs: 'h-6 px-2 text-xs',
} as const;

const BUTTON_KIND = {
  /** The one main action of a view: NDI navy. */
  primary:
    'border-(--color-primary) bg-(--color-primary) text-(--color-primary-content) enabled:hover:border-(--color-primary-hover) enabled:hover:bg-(--color-primary-hover)',
  /** A second action that stands on its own: a hairline on the surface. */
  plain:
    'border-(--sdr-line-strong) bg-(--color-base-100) text-(--color-base-content) enabled:hover:bg-(--sdr-hover)',
  /** A quiet action next to others: no edge until hovered. */
  quiet: 'border-transparent bg-transparent text-(--color-base-content) enabled:hover:bg-(--sdr-hover)',
} as const;

/** The classes of a button: `primary` (one per view), `plain` or `quiet`; `sm` or `xs`. */
export function buttonClass(kind: keyof typeof BUTTON_KIND, size: keyof typeof BUTTON_SIZE = 'sm'): string {
  return `${BUTTON} ${BUTTON_SIZE[size]} ${BUTTON_KIND[kind]}`;
}

const FIELD =
  'min-w-0 rounded-lg border border-(--sdr-line-strong) bg-(--color-base-100) text-(--color-base-content) placeholder:text-(--sdr-ink-subtle) transition-[border-color,box-shadow] focus:border-(--sdr-focus) focus:shadow-[0_0_0_3px_color-mix(in_oklab,var(--sdr-focus)_22%,transparent)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-(--color-error)';

/** A text or number field, and a select. */
export const FIELD_CLASS = {
  sm: `${FIELD} h-8 px-2.5 text-sm`,
  xs: `${FIELD} h-7 px-2 text-xs`,
} as const;

/** A textarea. */
export const TEXTAREA_CLASS = `${FIELD} block w-full px-2.5 py-1.5 text-sm leading-5`;

/** A native file input, its button drawn like a plain button. */
export const FILE_CLASS =
  'block w-full text-sm text-(--sdr-ink-muted) file:mr-3 file:h-8 file:cursor-pointer file:rounded-lg file:border file:border-solid file:border-(--sdr-line-strong) file:bg-(--color-base-100) file:px-3 file:text-sm file:font-medium file:text-(--color-base-content) hover:file:bg-(--sdr-hover) disabled:cursor-not-allowed disabled:opacity-60';

/** A checkbox in the theme's primary colour. */
export const CHECKBOX_CLASS = 'h-4 w-4 shrink-0 cursor-pointer accent-(--color-primary)';

/** A card: the surface on a hairline, no shadow (as .card on today's pages). */
export const CARD_CLASS = 'rounded-xl border border-(--sdr-line) bg-(--color-base-100)';

/** A link in running text: underlined in the line colour, the full colour on hover. */
export const LINK_CLASS =
  'underline decoration-(--sdr-line-strong) underline-offset-2 transition-colors hover:decoration-current';

/** A warning or a problem to act on: a thin amber rule on the left, muted text — calm, but not content. */
export const WARNING_CLASS = 'border-l-2 border-(--color-warning) pl-3 text-(--sdr-ink-muted)';

/** A fold: FOLD_CLASS on the <details>, foldSummary() as its first child, then its body (with its own top margin). */
export const FOLD_CLASS = 'group/fold';

/** A fold's summary: a quiet line with a chevron that turns when it opens. */
export function foldSummary(label: string): TemplateResult {
  return html`<summary
    class="flex w-fit cursor-pointer list-none items-center gap-1 rounded text-sm text-(--sdr-ink-muted) transition-colors hover:text-(--color-base-content) [&::-webkit-details-marker]:hidden"
    ><span class="flex transition-transform group-open/fold:rotate-90">${icon({ name: 'chevron-right', size: 14 })}</span
    >${label}</summary
  >`;
}

/** Two or more choices of which one is on (Link / Upload). */
export const SEGMENTS_CLASS = 'inline-flex rounded-lg border border-(--sdr-line) bg-(--color-base-100) p-0.5';
export const SEGMENT_CLASS = {
  on: 'h-7 rounded-md px-3 text-sm font-medium bg-(--sdr-sunken) text-(--color-base-content) shadow-[inset_0_0_0_1px_var(--sdr-line)]',
  off: 'h-7 rounded-md px-3 text-sm text-(--sdr-ink-muted) transition-colors hover:bg-(--sdr-hover) hover:text-(--color-base-content)',
} as const;

const SPINNER_SIZE = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
} as const;

/** A small turning ring in the colour of the text around it; it stops under reduced motion. */
export function spinner(size: keyof typeof SPINNER_SIZE = 'xs'): TemplateResult {
  return html`<span
    class="${SPINNER_SIZE[size]} inline-block shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent"
    aria-hidden="true"
  ></span>`;
}
